package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import br.gov.go.pge.permanenciaweb.shared.OccupantDto
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.pages.DashboardState
import br.gov.go.pge.permanenciaweb.site.pages.ROSTER_PAGE_SIZE
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.FontVariantNumeric
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontVariant
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.dom.ref
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Br
import org.jetbrains.compose.web.dom.Button as HtmlButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

/** Page column for the roster tab: the roster panel and, below it, the removed/history panel. */
val RosterLayoutStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Column)
        .gap(1.cssRem)
        .padding(top = 0.cssRem, right = 1.5.cssRem, bottom = 1.5.cssRem, left = 1.5.cssRem)
}

val TableStyle = CssStyle.base {
    // Escape hatch: `border-collapse` has no TableModifiers helper.
    Modifier
        .width(100.percent)
        .styleModifier { property("border-collapse", "collapse") }
}

val TableCellStyle = CssStyle.base {
    Modifier
        .textAlign(TextAlign.Start)
        .padding(0.5.cssRem, 0.75.cssRem)
        // 1px hairline dividers stay in px per the units policy.
        .borderBottom(1.px, LineStyle.Solid, IndustrialPalette.Grid)
        .whiteSpace(WhiteSpace.NoWrap)
}

val TableHeaderCellStyle = TableCellStyle.extendedByBase {
    Modifier
        .fontSize(0.6875.cssRem)
        .textTransform(TextTransform.Uppercase)
        .letterSpacing(0.04.em)
        .color(IndustrialPalette.Muted)
}

val TableNumericCellStyle = TableCellStyle.extendedByBase {
    Modifier.fontVariant { numeric(FontVariantNumeric.TabularNums) }
}

val TableNumericHeaderCellStyle = TableHeaderCellStyle.extendedByBase {
    Modifier.fontVariant { numeric(FontVariantNumeric.TabularNums) }
}

val TableRowHoverStyle = CssStyle {
    cssRule(":hover td") { Modifier.backgroundColor(IndustrialPalette.RowHover) }
}

/**
 * Horizontal scroll container for tables that can outgrow their panel (long names, many chips).
 * Keeps the page free of a page-level horizontal scrollbar: the table scrolls inside the panel,
 * whose `overflow:hidden` still clips the rounded corners.
 */
val TableScrollStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .overflow(Overflow.Auto, Overflow.Hidden)
}

@Composable
fun TableScroll(content: @Composable () -> Unit) {
    Div(attrs = TableScrollStyle.toAttrs()) {
        content()
    }
}

sealed interface AlertChipKind : ComponentKind

val AlertChipStyle = CssStyle<AlertChipKind> {
    base {
        Modifier
            .display(DisplayStyle.InlineBlock)
            .padding(0.0625.cssRem, 0.5.cssRem)
            .borderRadius(0.5.cssRem)
            .fontSize(0.6875.cssRem)
            .fontWeight(FontWeight.SemiBold)
    }
}

val AlertInconsistentVariant = AlertChipStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.RedTint).color(IndustrialPalette.Red)
}

/** After-hours permit: informational, not a problem. */
val AlertReleasedVariant = AlertChipStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.SteelTint).color(IndustrialPalette.Steel)
}

/** Permit that already ended: neutral dark gray, light text — closed, not an error. */
val AlertExpiredVariant = AlertChipStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.ExpiredBg).color(IndustrialPalette.ExpiredFg)
}

/** Recurring end-of-day missed exits in the rolling window. */
val AlertSemSaidaVariant = AlertChipStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.OrangeTint).color(IndustrialPalette.Orange)
}

val AlertEmptyVariant = AlertChipStyle.addVariantBase {
    Modifier.color(IndustrialPalette.Muted)
}

/**
 * Shared chip visual. The optional [tooltip] is exposed as a native title and repeated in a
 * visually hidden span for screen readers, since `title` alone is hover-only.
 */
@Composable
private fun AlertChipContent(
    text: String,
    dataAlert: String,
    variant: CssStyleVariant<AlertChipKind>,
    secondary: String? = null,
    tooltip: String? = null,
) {
    Span(
        attrs = AlertChipStyle.toModifier(variant).toAttrs {
            attr("data-alert-chip", dataAlert)
            if (tooltip != null) attr("title", tooltip)
        },
    ) {
        Text(text)
        if (secondary != null) {
            Br()
            Text(secondary)
        }
        if (tooltip != null) {
            Span(attrs = ScreenReaderOnlyStyle.toAttrs()) { Text(" — $tooltip") }
        }
    }
}

/** Roster rows after every active filter (KPI cards + columns) and the sort. */
internal fun DashboardState.rosterRows(occupants: List<OccupantDto>): List<OccupantDto> =
    occupants
        .applyQuickFilters(quickFilters)
        .applyColumnFilters(
            filters = filters.forTable(ROSTER_FILTER_KEYS),
            text = { o, key -> key.rosterText(o) },
            // "Alerta" is the only multi-valued column: one token per code, so a row with two
            // alerts matches either checkbox.
            values = { o, key -> if (key == FilterKey.ALERTA) o.alertasLabels else listOfNotNull(key.rosterText(o)) },
        )
        .sortedWithCriteria(rosterSort) { o, field -> field.text(o) }

@Composable
internal fun RosterPanel(
    state: DashboardState,
    occupants: List<OccupantDto>,
    facets: Map<FilterKey, List<String>>,
    onSemSaida: (OccupantDto) -> Unit = {},
    onPhotos: (OccupantDto) -> Unit = {},
) {
    val snapshot = state.snapshot

    val rows = state.rosterRows(occupants)

    val pages = pageCountOf(rows.size, ROSTER_PAGE_SIZE)
    val currentPage = state.rosterPage.coerceIn(1, pages.coerceAtLeast(1))
    val visibleRows = rows.pageSlice(currentPage, ROSTER_PAGE_SIZE)
    val goToPage: (Int) -> Unit = { page ->
        state.rosterPage = page
        document.getElementById("roster")?.scrollIntoView()
    }

    Panel(modifier = Modifier.id("roster")) {
        PanelHeader(
            title = {
                Text("Possíveis ocupantes ")
                Span {
                    Text(
                        when {
                            snapshot == null -> ""
                            rows.size != occupants.size -> "— ${rows.size} de ${occupants.size}"
                            else -> "— ${rows.size}"
                        }
                    )
                }
            },
            actions = { ClearFiltersButton(state, ROSTER_SCOPE) },
        )
        RosterTable(
            rows = visibleRows,
            sort = state.rosterSort,
            filters = state.filters,
            facets = facets,
            canRemove = state.me?.canManageRosterCorrections == true,
            onRemove = { state.openRemoval(it) },
            onSort = { field, shift -> state.toggleRosterSort(field, shift) },
            onFilterChange = { key, filter -> state.updateFilter(key, filter) },
            onSemSaida = onSemSaida,
            onPhotos = onPhotos,
        )
        if (snapshot != null && rows.isEmpty()) {
            P(attrs = PanelEmptyStyle.toAttrs()) { Text("Nenhum ocupante para os filtros atuais.") }
        }
        Pagination(page = currentPage, pageCount = pages, onPage = goToPage)
    }
}

/** "Limpar tudo" for one table: clears the shared filters plus that table's own columns/cards. */
@Composable
internal fun ClearFiltersButton(state: DashboardState, scope: FilterScope) {
    Button(
        onClick = { state.clearFilters(scope) },
        variant = LinkButtonVariant,
        size = ButtonSize.SM,
        enabled = state.hasFilters(scope),
    ) {
        Text("Limpar tudo")
    }
}

@Composable
private fun RosterTable(
    rows: List<OccupantDto>,
    sort: List<Criterion<RosterSortField>>,
    filters: Map<FilterKey, ColumnFilter>,
    facets: Map<FilterKey, List<String>>,
    canRemove: Boolean,
    onRemove: (OccupantDto) -> Unit,
    onSort: (RosterSortField, Boolean) -> Unit,
    onFilterChange: (FilterKey, ColumnFilter) -> Unit,
    onSemSaida: (OccupantDto) -> Unit,
    onPhotos: (OccupantDto) -> Unit,
) {
    Table(attrs = TableStyle.toAttrs()) {
        Thead {
            Tr {
                RosterHeaderCell("Nome", RosterSortField.NOME, sort, filters, facets, onSort, onFilterChange)
                RosterHeaderCell("Cargo", RosterSortField.CARGO, sort, filters, facets, onSort, onFilterChange)
                RosterHeaderCell("Telefone", RosterSortField.TELEFONE, sort, filters, facets, onSort, onFilterChange)
                RosterHeaderCell("Sala", RosterSortField.SALA, sort, filters, facets, onSort, onFilterChange)
                RosterHeaderCell("Andar", RosterSortField.ANDAR, sort, filters, facets, onSort, onFilterChange)
                RosterHeaderCell("Unidade", RosterSortField.UNIDADE, sort, filters, facets, onSort, onFilterChange)
                // Só o rótulo muda: o campo continua `tipoAcesso` / `RosterSortField.TIPO`.
                RosterHeaderCell("Cad. Portaria", RosterSortField.TIPO, sort, filters, facets, onSort, onFilterChange)
                RosterHeaderCell(
                    "Entrada",
                    RosterSortField.ENTRADA,
                    sort,
                    filters,
                    facets,
                    onSort,
                    onFilterChange,
                    numeric = true,
                )
                RosterHeaderCell("Alerta", RosterSortField.ALERTA, sort, filters, facets, onSort, onFilterChange)
                // Actions column: nothing to search, so no magnifier (documented deviation).
                Th(attrs = TableHeaderCellStyle.toModifier().toAttrs { attr("scope", "col") }) { }
            }
        }
        Tbody {
            for (o in rows) {
                Tr(attrs = TableRowHoverStyle.toAttrs()) {
                    Td(attrs = TableCellStyle.toAttrs()) { Text(o.nome) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(o.cargo.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(o.telefone.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(o.sala.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(o.andar.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(o.unidade.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(o.tipoAcesso.orEmpty()) }
                    Td(attrs = TableNumericCellStyle.toAttrs()) { Text(o.entrada) }
                    Td(attrs = TableCellStyle.toAttrs()) {
                        if (o.alertas.isEmpty()) {
                            AlertChipContent(text = "—", dataAlert = "empty", variant = AlertEmptyVariant)
                        } else {
                            Div(attrs = Modifier.display(DisplayStyle.Flex).gap(0.25.cssRem).toAttrs()) {
                                for (label in o.alertasLabels) {
                                    val variant = when (label) {
                                        ALERTA_LIBERADO -> AlertReleasedVariant
                                        ALERTA_EXPIRADO -> AlertExpiredVariant
                                        ALERTA_SEM_SAIDA -> AlertSemSaidaVariant
                                        else -> AlertInconsistentVariant
                                    }
                                    val chipText = if (label == ALERTA_SEM_SAIDA && o.diasSemSaida30 != null) {
                                        "$label: ${o.diasSemSaida30}d"
                                    } else {
                                        label
                                    }
                                    val tooltip = if (label == ALERTA_INCONSISTENTE) {
                                        ALERTA_INCONSISTENTE_TOOLTIP
                                    } else {
                                        null
                                    }

                                    if (label == ALERTA_SEM_SAIDA) {
                                        HtmlButton(
                                            attrs = Modifier
                                                .alignItems(AlignItems.Center)
                                                // The button is only the hit target; the styled chip is
                                                // the span inside it, so the global `button { font: inherit }`
                                                // rule cannot alter the chip typography.
                                                .styleModifier {
                                                    property("display", "inline-flex")
                                                    property("appearance", "none")
                                                    property("padding", "0")
                                                    property("border", "0")
                                                    property("background", "transparent")
                                                    property("font", "inherit")
                                                    property("cursor", "pointer")
                                                }
                                                .toAttrs {
                                                    attr("type", "button")
                                                    attr("data-sem-saida", o.idpessoa)
                                                    attr("title", "Ver dias sem saída")
                                                    onClick { onSemSaida(o) }
                                                },
                                        ) {
                                            AlertChipContent(text = chipText, dataAlert = label, variant = variant)
                                            // Screen readers keep the visible chip text and also hear
                                            // what activating the button does for this person.
                                            Span(attrs = ScreenReaderOnlyStyle.toAttrs()) {
                                                Text(" — Ver dias sem saída de ${o.nome}")
                                            }
                                        }
                                    } else {
                                        AlertChipContent(
                                            text = chipText,
                                            dataAlert = label,
                                            variant = variant,
                                            secondary = if (label == ALERTA_LIBERADO || label == ALERTA_EXPIRADO) {
                                                o.intervaloLiberacao()
                                            } else {
                                                null
                                            },
                                            tooltip = tooltip,
                                        )
                                    }
                                }
                            }
                        }
                    }
                    Td(attrs = TableCellStyle.toAttrs()) {
                        Div(attrs = Modifier.display(DisplayStyle.Flex).gap(0.375.cssRem).toAttrs()) {
                            Button(
                                onClick = { onPhotos(o) },
                                variant = DefaultButtonVariant,
                                size = ButtonSize.SM,
                                ref = ref { element ->
                                    element.setAttribute("aria-label", "Ver fotos de ${o.nome}")
                                    element.title = "Ver fotos"
                                },
                            ) {
                                CameraIcon(Modifier.size(0.875.cssRem))
                            }
                            if (canRemove) {
                                Button(
                                    onClick = { onRemove(o) },
                                    variant = DangerButtonVariant,
                                    size = ButtonSize.SM,
                                ) {
                                    Text("Remover")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun RosterHeaderCell(
    label: String,
    field: RosterSortField,
    sort: List<Criterion<RosterSortField>>,
    filters: Map<FilterKey, ColumnFilter>,
    facets: Map<FilterKey, List<String>>,
    onSort: (RosterSortField, Boolean) -> Unit,
    onFilterChange: (FilterKey, ColumnFilter) -> Unit,
    numeric: Boolean = false,
) = SortableTh(
    label = label,
    field = field,
    criteria = sort,
    filter = filters[field.filterKey] ?: ColumnFilter.Empty,
    filterId = "roster-${field.name.lowercase()}",
    options = facets[field.filterKey],
    numeric = numeric,
    onSort = onSort,
    onFilterChange = { onFilterChange(field.filterKey, it) },
)
